import { TNotification } from '@/features/notifications/schemas/notifications.schemas.ts';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/utils/utils.ts';
import { useMediaQuery } from '@/hooks/use-media-query.tsx';
import { useDeleteNotificationQuery } from '@/features/notifications/hooks/mutations/use-delete-notification.ts';
import { DotsVerticalIcon, TrashIcon, CheckIcon } from '@radix-ui/react-icons';
import { useState } from 'react';
import { LoaderIcon } from 'lucide-react';
import { useUpdateNotificationQuery } from '@/features/notifications/hooks/mutations/use-patch-notification.ts';
import { useNotificationsStore } from '@/services/store/use-notifications-store.ts';

export function NotificationCardPopover({
  notification: { notificationId, status },
}: {
  notification: TNotification;
}) {
  const notificationFilter = useNotificationsStore((state) => state.notificationFilter);
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');

  const { mutate: deleteNotification, isPending: isDeleting } = useDeleteNotificationQuery();
  const { mutate: updateNotification, isPending: isUpdating } = useUpdateNotificationQuery({
    notificationFilter,
  });

  const handleDelete = () => {
    setIsOpen(false);
    deleteNotification({ notificationId });
  };

  const toggledStatus = status === 'unread' ? 'read' : 'unread';

  const handleMarkAsRead = () => {
    updateNotification({ notificationId, status: toggledStatus });
    setIsOpen(false);
  };

  const isPending = isDeleting || isUpdating;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          disabled={isDeleting}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-xl',
            'text-muted-foreground transition-all duration-200 ease-in-out',
            'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',

            'hover:text-foreground hover:bg-white',
            'active:scale-95',

            'disabled:cursor-not-allowed disabled:opacity-50',

            isMobile || isPending ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          )}
          aria-label="Notification options"
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          {isPending && (
            <LoaderIcon className="size-6 animate-spin text-gray-800" aria-hidden="true" />
          )}

          {!isPending && <DotsVerticalIcon className="h-6 w-6" />}
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-48 p-1" align="end" sideOffset={4}>
        <div className="space-y-1" role="menu">
          <button
            onClick={handleMarkAsRead}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
              'text-left',
              'hover:bg-accent hover:text-accent-foreground',
              'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
              'disabled:pointer-events-none disabled:opacity-50'
            )}
            role="menuitem"
          >
            <CheckIcon className="h-4 w-4" />
            Mark as {toggledStatus}
          </button>

          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
              'text-left',
              'hover:bg-accent hover:text-accent-foreground',
              'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
              'disabled:pointer-events-none disabled:opacity-50'
            )}
            role="menuitem"
          >
            <TrashIcon className="h-4 w-4" />
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
